/**
 * Dropdown — the debug panel's one select control, single- or multi-choice.
 *
 * Not a native `<select>`: the multi variant would need ctrl-click, which does not exist on a phone,
 * and the panel is read on one. A button showing the current value opens a popover of options —
 * radio behaviour when `value` is given, checkbox behaviour when `values` is.
 */

import { useEffect, useRef, useState } from 'react';
import { ChevronDown, Check } from 'lucide-react';

export interface DropdownOption<T> {
  value: T;
  label: string;
  /** Hover explanation for this option. */
  title?: string;
  /** Swatch shown before the label (the LOD legend uses it). */
  color?: string;
}

interface DropdownProps<T> {
  /** Shown on the button when nothing is selected (multi) or as a prefix (both). */
  label: string;
  options: readonly DropdownOption<T>[];
  /** Single-select: the selected value. Mutually exclusive with `values`. */
  value?: T;
  /** Multi-select: the selected values. Mutually exclusive with `value`. */
  values?: readonly T[];
  onSelect?: (value: T) => void;
  onToggle?: (value: T) => void;
  title?: string;
}

export function Dropdown<T extends string | number>({
  label, options, value, values, onSelect, onToggle, title,
}: DropdownProps<T>) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const multi = values !== undefined;

  // Click-away close — the panel is a stack of these, and leaving two open overlaps them.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    window.addEventListener('mousedown', onDown);
    return () => window.removeEventListener('mousedown', onDown);
  }, [open]);

  const selected = multi
    ? options.filter((o) => values!.includes(o.value))
    : options.filter((o) => o.value === value);
  const summary = selected.length === 0
    ? 'off'
    : selected.map((o) => o.label).join(', ');

  const isOn = (v: T) => (multi ? values!.includes(v) : value === v);

  return (
    <div className="relative mb-0.5" ref={ref} title={title}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center gap-1 bg-black/60 border border-green-500/40 rounded px-1 py-0.5 text-left cursor-pointer hover:border-green-500/80"
      >
        <span className="text-green-500 shrink-0">{label}</span>
        <span className={`flex-1 min-w-0 truncate ${selected.length ? 'text-yellow-300' : 'text-yellow-300/40'}`}>
          {summary}
        </span>
        <ChevronDown size={11} className="text-green-500/60 shrink-0" />
      </button>
      {open && (
        <div className="absolute z-20 mt-0.5 w-full max-h-52 overflow-y-auto scrollbar-compact bg-black/95 border border-green-500/40 rounded py-0.5">
          {options.map((o) => (
            <button
              key={String(o.value)}
              title={o.title}
              onClick={() => {
                if (multi) onToggle?.(o.value);
                else { onSelect?.(o.value); setOpen(false); }
              }}
              className={`w-full flex items-center gap-1.5 px-1.5 py-0.5 text-left cursor-pointer hover:bg-green-500/15 ${
                isOn(o.value) ? 'text-yellow-300' : 'text-yellow-300/60'
              }`}
            >
              <span className="w-3 shrink-0">
                {isOn(o.value) && <Check size={11} />}
              </span>
              {o.color && (
                <span
                  className="inline-block w-2.5 h-2.5 rounded-sm border border-black/40 shrink-0"
                  style={{ backgroundColor: o.color }}
                />
              )}
              <span className="truncate">{o.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
